import React, { useState } from 'react';
import { Card, Grid, NoData, Select } from '../ui.jsx';
import { TTC_COLORS } from '../admin/Timetable';

function TeacherTimetable({ db, teacher, name }) {
  const [ttCls, setTtCls] = useState(teacher.cls || '');
  const classes = db.classes.map(c => c.name);
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const todayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date().getDay()];

  // Today's periods for THIS teacher across all classes
  const todayPeriods = db.tt
    .filter(p => p.day === todayName && p.ty === 'subject' && (p.tea === name || (teacher.ln && p.tea?.includes(teacher.ln))))
    .map(p => {
      const slot = db.slots.find(s => s.id === p.sid);
      return { ...p, slot };
    })
    .sort((a, b) => (a.slot?.s || '').localeCompare(b.slot?.s || ''));

  const pers = db.tt.filter(p => p.cls === ttCls);
  const scm = {}; let ci = 0;
  pers.forEach(p => { if (p.su && !scm[p.su]) { scm[p.su] = ci % TTC_COLORS.length; ci++; } });

  const isSunday = todayName === 'Sunday';

  return (
    <div className="teacher-timetable-page">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif', margin: 0 }}>Timetable</h1>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Your schedule & class timetable</p>
      </div>

      {/* Today's Schedule Card */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)', marginBottom: 24, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg,#1960a3,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 17, color: '#fff', fontVariationSettings: "'FILL' 1" }}>today</span>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>Today's Schedule</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{todayName} · {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          </div>
          {!isSunday && <div style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#1960a3', background: '#eef4ff', border: '1px solid #c7d9f5', borderRadius: 20, padding: '3px 12px' }}>{todayPeriods.length} period{todayPeriods.length !== 1 ? 's' : ''}</div>}
        </div>
        {isSunday ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 36, display: 'block', marginBottom: 8 }}>weekend</span>
            <div style={{ fontSize: 13, fontWeight: 600 }}>It's Sunday — enjoy your day off!</div>
          </div>
        ) : todayPeriods.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 36, display: 'block', marginBottom: 8 }}>event_available</span>
            <div style={{ fontSize: 13, fontWeight: 600 }}>No periods assigned for today</div>
          </div>
        ) : (
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {todayPeriods.map((p, i) => {
              const col = TTC_COLORS[i % TTC_COLORS.length];
              return (
                <div key={p.id || i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 12, background: col.bg, border: `1.5px solid ${col.ring}` }}>
                  <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: col.fg, flexShrink: 0 }} />
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: col.fg, fontVariationSettings: "'FILL' 1" }}>menu_book</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: col.fg }}>{p.su}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Class {p.cls}{p.rm ? ` · Room ${p.rm}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {p.slot ? <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{p.slot.s} – {p.slot.e}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>{p.slot.l}</div>
                    </> : <div style={{ fontSize: 11, color: '#94a3b8' }}>—</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Class Timetable Grid */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf5', boxShadow: '0 2px 12px rgba(0,32,69,0.06)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#64748b' }}>grid_view</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Class Timetable</span>
          <select value={ttCls} onChange={e => setTtCls(e.target.value)} style={{ marginLeft: 'auto', padding: '7px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit', color: '#1e293b', background: '#fff', cursor: 'pointer' }}>
            <option value="">Select Class</option>{classes.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        {!ttCls ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 40, display: 'block', marginBottom: 8 }}>schedule</span>
            <div style={{ fontSize: 13 }}>Select a class to view timetable</div>
          </div>
        ) : (
          <div style={{ padding: 20, overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '90px repeat(' + DAYS.length + ',1fr)', gap: 3, minWidth: 600 }}>
              <div style={{ padding: '8px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#64748b', background: '#f8fafc', borderRadius: 8 }}>Time</div>
              {DAYS.map(d => <div key={d} style={{ padding: '8px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#64748b', background: '#f8fafc', borderRadius: 8 }}>{d.slice(0, 3)}</div>)}
              {db.slots.map(sl => (
                <React.Fragment key={sl.id}>
                  <div style={{ padding: '8px', textAlign: 'center', background: 'rgba(241,245,249,0.6)', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#1e293b' }}>{sl.l}</div>
                    <div style={{ fontSize: 9, color: '#94a3b8' }}>{sl.s}–{sl.e}</div>
                  </div>
                  {DAYS.map(day => {
                    const per = pers.find(x => x.day === day && x.sid === sl.id);
                    if (per) {
                      let ck = scm[per.su] ?? 0;
                      if (per.ty === 'break' || per.ty === 'assembly' || per.ty === 'free') ck = null;
                      const c = ck !== null ? (TTC_COLORS[ck] || TTC_COLORS[0]) : { bg: 'rgba(241,245,249,0.8)', fg: '#64748b', ring: '#e2e8f0' };
                      const lb = per.ty === 'break' ? 'Break' : per.ty === 'assembly' ? 'Assembly' : per.ty === 'free' ? 'Free' : per.su || '—';
                      const isMe = per.tea === name || (teacher.ln && per.tea?.includes(teacher.ln));
                      return <div key={day} style={{ padding: '7px 5px', borderRadius: 8, textAlign: 'center', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', background: c.bg, color: c.fg, outline: isMe ? `2px solid ${c.fg}` : 'none', outlineOffset: '-2px' }}>
                        <div style={{ fontSize: 11, fontWeight: 700 }}>{lb}</div>
                        {per.rm && <div style={{ fontSize: '9px', opacity: .7 }}>Rm {per.rm}</div>}
                        {isMe && <div style={{ fontSize: '9px', fontWeight: 800, opacity: .8 }}>★ You</div>}
                      </div>;
                    }
                    return <div key={day} style={{ padding: '7px 5px', borderRadius: 8, textAlign: 'center', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(241,244,246,0.5)', color: '#c4c6cf', fontSize: 18 }}>—</div>;
                  })}
                </React.Fragment>
              ))}
            </div>
            {Object.keys(scm).length > 0 && <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
              {Object.keys(scm).map(su => { const c = TTC_COLORS[scm[su]] || TTC_COLORS[0]; return <div key={su} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}><div style={{ width: 10, height: 10, borderRadius: 2, background: c.bg, border: '1.5px solid ' + c.fg }} />{su}</div>; })}
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}


export default TeacherTimetable;
